import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { ChevronDown, ChevronRight, Gem, ArrowRight } from 'lucide-react';

const Answer = ({ answer, comparedWith }) => (
  <div className="space-y-2" data-testid={`checkin-answer-${answer.field_id}`}>
    <p className="text-sm text-white/60">{answer.label}</p>
    {answer.value ? (
      <p className="text-sm text-white/90 whitespace-pre-line leading-relaxed rounded-lg
                    border border-white/10 bg-black/25 px-4 py-3">
        {answer.value}
      </p>
    ) : (
      <p className="text-xs text-white/30 italic">Left blank</p>
    )}
    {answer.previous && (
      <div className="rounded-lg border border-violet-300/20 bg-violet-400/[0.06] px-4 py-3"
           data-testid={`checkin-previous-${answer.field_id}`}>
        <p className="text-[11px] uppercase tracking-wider text-violet-200/70 mb-1.5">
          What you wrote in {comparedWith}
        </p>
        <p className="text-sm text-white/60 whitespace-pre-line leading-relaxed">
          {answer.previous}
        </p>
      </div>
    )}
  </div>
);

const CheckIn = ({ row, openByDefault }) => {
  const [open, setOpen] = useState(openByDefault);
  const Chevron = open ? ChevronDown : ChevronRight;
  const answered = row.sections.reduce(
    (n, s) => n + s.answers.filter((a) => a.value).length, 0);

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden"
         data-testid={`checkin-${row.worksheet_id}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-start justify-between gap-3 px-4 py-3 text-left
                   hover:bg-white/[0.04] transition-colors duration-200"
        data-testid={`checkin-toggle-${row.worksheet_id}`}
      >
        <span className="flex items-start gap-2 min-w-0">
          <Chevron className="w-4 h-4 text-white/40 shrink-0 mt-0.5" />
          <span className="min-w-0">
            <span className="block text-sm text-white/90 truncate">{row.title}</span>
            <span className="block text-xs text-white/40 mt-0.5">
              {row.status === 'submitted'
                ? `Completed ${row.submitted_at ? new Date(row.submitted_at).toLocaleDateString() : ''}`
                : row.status === 'not_started' ? 'Not started' : 'In progress'}
              {answered ? ` · ${answered} answered` : ''}
              {row.compared_with ? ` · beside ${row.compared_with}` : ''}
            </span>
          </span>
        </span>
        <Link
          to={`/dashboard/modules/${row.module_id}`}
          onClick={(e) => e.stopPropagation()}
          className="text-[11px] text-cyan-300 hover:text-cyan-200 shrink-0"
          data-testid={`checkin-open-${row.worksheet_id}`}
        >
          {row.status === 'submitted' ? 'Open it' : 'Fill it in'} →
        </Link>
      </button>

      {open && (
        <div className="px-4 pb-5 space-y-6" data-testid={`checkin-detail-${row.worksheet_id}`}>
          {answered === 0 && (
            <p className="text-sm text-white/45">
              Nothing written here yet.{' '}
              <Link to={`/dashboard/modules/${row.module_id}`}
                    className="text-cyan-300 hover:text-cyan-200">Open the check-in →</Link>
            </p>
          )}
          {row.sections.filter((s) => s.answers.some((a) => a.value || a.previous))
            .map((section, i) => (
            <section key={i} className="space-y-4">
              {section.heading && (
                <h3 className="text-sm font-semibold text-white/85 pt-2">{section.heading}</h3>
              )}
              {section.answers.filter((a) => a.value || a.previous).map((answer) => (
                <Answer key={answer.field_id} answer={answer}
                        comparedWith={row.compared_with} />
              ))}
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Values, beliefs and intentions — the three he narrowed to, and every check-in since, each
 * answer sitting beside what he wrote to the same question last time.
 */
export const ValuesSpace = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/companion/check-ins`)
      .then((r) => setData(r.data))
      .catch(() => setData({ values: [], check_ins: [] }));
  }, []);

  if (!data) {
    return <p className="text-sm text-white/50" data-testid="values-loading">Opening your values…</p>;
  }

  return (
    <div className="space-y-10" data-testid="values-space">
      <section className="space-y-3" data-testid="values-three">
        <div>
          <h2 className="text-base md:text-lg font-semibold text-white flex items-center gap-2">
            <Gem className="w-4 h-4 text-amber-200" /> Your three values
          </h2>
          <p className="text-sm text-white/45">
            In your own words, from Values and Beliefs in Module 2.
          </p>
        </div>
        {data.values?.length ? (
          <div className="flex flex-wrap gap-2">
            {data.values.map((value) => (
              <span key={value}
                className="rounded-full border border-amber-300/30 bg-amber-300/10 px-4 py-1.5
                           text-sm text-amber-50">
                {value}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-sm text-white/45">
            Narrow your values to three in Module 2 and they will live here.{' '}
            <Link to="/dashboard/modules" className="text-cyan-300 hover:text-cyan-200"
                  data-testid="values-go-modules">Open the program →</Link>
          </p>
        )}
      </section>

      <section className="space-y-3" data-testid="values-checkins">
        <div>
          <h2 className="text-base md:text-lg font-semibold text-white">Your check-ins</h2>
          <p className="text-sm text-white/45">
            Not evaluations — gentle pauses. Each answer sits beside what you wrote to the same
            question last time, so you can see what has shifted.
          </p>
        </div>

        {data.check_ins?.length ? (
          <div className="space-y-2">
            {data.check_ins.map((row, i) => (
              <CheckIn key={row.worksheet_id} row={row} openByDefault={i === 0} />
            ))}
          </div>
        ) : (
          <p className="text-sm text-white/45" data-testid="values-checkins-empty">
            No check-ins are open to you yet. When one arrives in your program it appears here,
            and everything you write in it gathers on this page.
          </p>
        )}

        {data.next && (
          <Link
            to={`/dashboard/modules/${data.next.module_id}`}
            className="inline-flex items-center gap-2 rounded-full border border-cyan-300/40
                       bg-cyan-400/15 px-5 py-2 text-sm text-cyan-50 hover:bg-cyan-400/25
                       transition-colors duration-200"
            data-testid="values-next-checkin"
          >
            {data.completed ? 'Your next check-in' : 'Start your first check-in'}: {data.next.title}
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        )}
      </section>
    </div>
  );
};

export default ValuesSpace;
